import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { deepEquals } from 'app/infrastructure/js';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { SerializePayload } from 'app/area/search-filters/types';
import { SearchFilters } from 'app/area/search-filters/search-filters';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { searchTranslations } from '../constants';
import { SearchResult } from '../search-container/search-result';
import { actionCreators } from '../actions';
import { AdministrationSearchState, SearchArea, SearchData, SearchResults } from '../types-store';
import { RoleTile } from 'app/area/administration/list/roles/role-tile';
import { RoleToolbar } from 'app/area/administration/list/roles/role-toolbar';
import { roleFilters, roleQueryFilters } from './role-filter-configuration';
import { serialize } from '../serialize/serialize';
import { SearchTitle } from 'app/area/search-filters/search-container/search-title';
import { DefaultRole } from 'app/area/administration/shared/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { extraInfoFilters, shouldLoadExtraInfo } from 'app/area/administration/list/filters/extra-info-filters';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type RoleSearchProps = DefaultContainerProps & AdministrationSearchState & typeof actionCreators;

class RoleSearch extends React.Component<RoleSearchProps> {

  public static statePath: string = STATE.administrationSearch;

  public componentDidMount() {
    scrollToTop();
    this.props.loadDefaultRole();
    this.loadExtraInfo();
  }

  public componentWillUnmount() {
    this.props.clearList();
  }

  private checkReadRoleAccess() {
    const { displayError,
      sharedState: { access: { adminPanel: { role: { read: readRoleAccess = false } } = {} } = {} } = {},
      translation
    } = this.props;

    if (!readRoleAccess) {
      displayError(translation.no_access_to_role_administration);
      navigate.toStartupView(true);
    }
  }

  private loadExtraInfo = () => {
    shouldLoadExtraInfo(this.props) && this.props.loadExtraInfo();
  };

  private updateSearchModel = (payload: SerializePayload) => {
    this.checkReadRoleAccess();
    this.onLoadData(serialize(payload));
  };

  private onLoadData = (searchModel: FilterSearchModel, startIndex?: number) => {

    const { loadRoles, requestedSearchModel } = this.props;

    if (startIndex !== undefined || !deepEquals(searchModel, requestedSearchModel)) {
      loadRoles(searchModel, startIndex);
    }
  };

  private onFetchMore = () => {

    const { results, requestedSearchModel } = this.props;

    if (results.fetching || !results.list || results.list.length === 0 || results.list.length === results.total) {
      return;
    }

    this.onLoadData(requestedSearchModel, results.list.length);
  };

  private onDelete = (id: number) => {

    const { deleteRole, translation } = this.props;

    deleteRole(id, translation.role_successfully_deleted);
  };

  private onExportToExcel = () => {
    const {
      results: {
        total
      }, translation
    } = this.props;

    total > 0 && this.props.roleExportToExcel(this.props.requestedSearchModel, total,
      translation.role_excel_started, translation.role_excel_success);
  };

  private resultsWithDefaultRole = (results: SearchResults, defaultRole: DefaultRole): SearchResults =>
    !!results?.list && isDefinedIncludingZero(defaultRole?.value)
      ? { ...results, list: results.list.map<SearchData>(item => item.id === defaultRole.value ? { ...item, isDefault: true } : item) }
      : results;

  private setDefaultRole = async (defaultRole: DefaultRole) => {
    await this.props.setDefaultRole(defaultRole, this.props.translation);
    await this.props.loadDefaultRole();
  };

  public render() {

    const { results, translation, loadRoleAccess, clearPersonAccess, exportUserExcelFetching,
      defaultRole, settingDefaultRoleId, extraInfoCodes,
      sharedState: { access: { adminPanel: { role: {
        create: createRoleAccess = false,
        read: readRoleAccess = false
      } = {}, restrictedAccessPanel } = {} } = {} } = {},
      requestedSearchModel: { defaultValue: { codetable = [] } = {} } = {} } = this.props;

    const roleToolbar = <RoleToolbar onExportToExcel={this.onExportToExcel}
      exportExcelFetching={exportUserExcelFetching}
      totalResultHits={results.total}
      translation={translation}
      canCreate={createRoleAccess}
      canRead={readRoleAccess} />;

    return <SearchFilters
      topToolbar={<SearchTitle title={translation.role_administration} />}
      rightToolbar={roleToolbar}
      filtersConfig={{ area: SearchArea.role }}
      queryFilters={roleQueryFilters(translation)}
      predefinedFilters={undefined}
      customFilters={extraInfoFilters(roleFilters({
        readOnlyDefaultFilterPickers: codetable.length === 0,
        hasRestrictedAccess: restrictedAccessPanel
      }, translation),
        extraInfoCodes, { shouldDisplay: false })}
      defaultFilterValues={undefined}
      updateSearchModel={this.updateSearchModel}>
      <SearchResult
        resultTile={RoleTile}
        results={this.resultsWithDefaultRole(results, defaultRole)}
        noHitLabel={translation.search_no_roles}
        canDuplicate={createRoleAccess}
        translation={translation}
        onFetchMore={this.onFetchMore}
        onDelete={this.onDelete}
        onLoadPersonAccess={loadRoleAccess}
        onClearPersonAccess={clearPersonAccess}
        setDefaultRole={this.setDefaultRole}
        settingDefaultRoleId={settingDefaultRoleId}
        showMeatball={createRoleAccess}
        defaultRole={defaultRole} />
    </SearchFilters>;
  }
}

const container = containerFactory(RoleSearch, actionCreators, undefined, searchTranslations);

export { container as RoleSearchView };
